const value = (source, ...keys) => keys.map((key) => source?.[key]).find((item) => item !== undefined && item !== null);
const lineNodeId = (line) => value(line, 'projectNodeId', 'project_node_id');
const linePhase = (line) => String(value(line, 'phase', 'phaseCode', 'phase_code') || 'GEN');
const lineLot = (line) => String(value(line, 'lot', 'lotCode', 'lot_code') || 'GEN');
const lineStatus = (line) => String(line.status || 'Non défini');
const lineTitle = (line) => String(line.title || line.denomination || line.codeGed || line.code_ged || 'Livrable');
const isComplete = (line) => lineStatus(line).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() === 'diffuse';

const summarize = (children) => {
  const deliverables = children.flatMap((child) => child.type === 'deliverable' ? [child] : child.metadata?.deliverables || []);
  const completedCount = deliverables.filter((child) => isComplete(child.metadata.line)).length;
  return { deliverables, deliverableCount: deliverables.length, completedCount,
    progress: deliverables.length ? Math.round((completedCount / deliverables.length) * 100) : 0 };
};

const groupLines = (lines, keyGetter, type, idPrefix, childBuilder) => [...new Set(lines.map(keyGetter))].sort().map((key) => {
  const children = childBuilder(lines.filter((line) => keyGetter(line) === key), key);
  return { id: `${idPrefix}:${key}`, type, label: key, children, metadata: { key, ...summarize(children) } };
}).filter((node) => node.metadata.deliverableCount > 0);

const buildDeliverables = (lines, prefix) => {
  const roots = lines.filter((line) => !value(line, 'parentId', 'parent_id'));
  const childrenByParent = lines.filter((line) => value(line, 'parentId', 'parent_id')).reduce((map, line) => {
    const parentId = String(value(line, 'parentId', 'parent_id'));
    map.set(parentId, [...(map.get(parentId) || []), line]); return map;
  }, new Map());
  return roots.map((line, index) => {
    const id = value(line, 'id') || `${prefix}:${index}`;
    const revisions = childrenByParent.get(String(id)) || [];
    const children = revisions.map((revision, revisionIndex) => ({ id: `deliverable:${value(revision, 'id') || `${id}:${revisionIndex}`}`,
      type: 'deliverable', label: lineTitle(revision), children: [], metadata: { line: revision, revision: true, deliverableCount: 1 } }));
    return { id: `deliverable:${id}`, type: 'deliverable', label: lineTitle(line), children,
      metadata: { line, revision: false, deliverableCount: 1, completedCount: isComplete(line) ? 1 : 0, progress: isComplete(line) ? 100 : 0 } };
  });
};

const buildScopeGroups = (lines, nodeId) => groupLines(lines, linePhase, 'phase', `node:${nodeId}:phase`, (phaseLines, phase) => (
  groupLines(phaseLines, lineLot, 'lot', `node:${nodeId}:phase:${phase}:lot`, (lotLines, lot) => buildDeliverables(lotLines, `${nodeId}:${phase}:${lot}`))
));

export const buildLdaTree = ({ project, resolvedNodes = [], lines = [] }) => {
  const nodes = resolvedNodes.filter((node) => node.generateLda !== false);
  const byIdentity = new Map(nodes.flatMap((node) => [[String(node.id), node], [String(node.key), node]]));
  const childrenByParent = new Map();
  nodes.forEach((node) => {
    const parentIdentity = value(node, 'parentKey', 'parent_key', 'parentNodeId', 'parent_node_id');
    const parent = parentIdentity === null || parentIdentity === undefined ? null : byIdentity.get(String(parentIdentity));
    const key = parent ? String(parent.id) : 'root';
    childrenByParent.set(key, [...(childrenByParent.get(key) || []), node]);
  });
  const knownNodeIds = new Set(nodes.map((node) => String(node.id)));
  const unassigned = lines.filter((line) => !knownNodeIds.has(String(lineNodeId(line))));
  const buildNode = (node) => {
    const nodeLines = lines.filter((line) => String(lineNodeId(line)) === String(node.id));
    const projectNodeChildren = (childrenByParent.get(String(node.id)) || []).map(buildNode);
    const groups = buildScopeGroups(nodeLines, node.id);
    const children = [...projectNodeChildren, ...groups];
    return { id: `project-node:${node.id}`, type: 'project_node', label: node.name || node.label || node.code,
      children, metadata: { node, code: node.code, ...summarize(children) } };
  };
  let projectChildren = (childrenByParent.get('root') || []).map(buildNode).filter((node) => node.metadata.deliverableCount > 0);
  if (unassigned.length) {
    const generalChildren = buildScopeGroups(unassigned, 'general');
    projectChildren = [{ id: 'project-node:general', type: 'project_node', label: 'Général', children: generalChildren,
      metadata: { code: 'GEN', ...summarize(generalChildren) } }, ...projectChildren];
  }
  const root = { id: `project:${project?.id || 'current'}`, type: 'project', label: project?.name || project?.label || 'Projet',
    children: projectChildren, metadata: { project, ...summarize(projectChildren) } };
  return root.metadata.deliverableCount ? root : { ...root, children: [] };
};

const matchesLine = (line, filters) => {
  const query = String(filters.search || '').trim().toLocaleLowerCase('fr');
  const haystack = [lineTitle(line), value(line, 'codeGed', 'code_ged'), value(line, 'documentType', 'document_type_code'), linePhase(line), lineLot(line)].join(' ').toLocaleLowerCase('fr');
  return (!query || haystack.includes(query)) && (!filters.phase || linePhase(line) === filters.phase)
    && (!filters.lot || lineLot(line) === filters.lot) && (!filters.status || lineStatus(line) === filters.status);
};

export const filterLdaTree = (node, filters = {}) => {
  if (node.type === 'deliverable') return matchesLine(node.metadata.line, filters) ? node : null;
  const children = node.children.map((child) => filterLdaTree(child, filters)).filter(Boolean);
  if (!children.length) return null;
  return { ...node, children, metadata: { ...node.metadata, ...summarize(children) } };
};

export const getLdaFilterOptions = (lines = []) => ({
  phases: [...new Set(lines.map(linePhase))].sort(),
  lots: [...new Set(lines.map(lineLot))].sort(),
  statuses: [...new Set(lines.map(lineStatus))].sort(),
});

export const collectLdaGroupIds = (node) => node ? [node.id, ...node.children.filter((child) => child.children.length).flatMap(collectLdaGroupIds)] : [];
